import { Component, OnInit, inject } from '@angular/core';
import { FormGroupService } from '../../services/form-group.service';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormGroup } from '../../data/form-group';
import {
  AdUser,
  BrowserMessage,
  BrowserMessagesService,
  BrowserMessageType,
  UserService,
  SideMenuHeadlineComponent, PeoplePickerComponent
} from '@rbb/ng-web-template-lib';
import { WebAuthService } from '@rbb/ng-web-auth-lib';
import { FormService } from '../../services/form.service';
import { FormListItem } from '../../data/form-list-item';
import { BodyFormGroup } from '../../body/body-form-group';
import { environment } from '../../../environments/environment';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-form-group',
  templateUrl: './form-group.component.html',
  styleUrls: ['./form-group.component.css'],
  imports: [FormsModule, RouterLink, SideMenuHeadlineComponent, PeoplePickerComponent]
})
export class FormGroupComponent implements OnInit {
  private formGroupService = inject(FormGroupService);
  private route = inject(ActivatedRoute);
  private browserMessagesService = inject(BrowserMessagesService);
  private userService = inject(UserService);
  private webAuthService = inject(WebAuthService);
  private formService = inject(FormService);
  private router = inject(Router);

  webUrl = environment.webUrl;

  id: string | undefined;
  formGroup: FormGroup | undefined;
  owners: AdUser[] = [];
  optionKeys = [''];
  optionValues = [''];
  linkKeys = [''];
  linkValues = [''];

  forms: FormListItem[] | undefined;

  constructor() {
    this.id = this.route.snapshot.params['id'];
  }

  ngOnInit(): void {
    this.getForms();
    if (this.id === 'new') {
      this.formGroup = new FormGroup();
      this.formGroup.owners = this.webAuthService.authUser?.username ? [this.webAuthService.authUser.username] : [];
      this.userService.getAdUsers(this.formGroup.owners).subscribe({
        next: (owners) => {
          this.owners = owners;
        },
        error: (error) => {
          this.browserMessagesService.showHttpError(error, 'Daten zu den Verantwortlichen konnten nicht geladen werden');
        }
      });
    } else {
      this.getFormGroup();
    }
  }

  getFormGroup(): void {
    if (this.id) {
      this.formGroupService.getFormGroup(this.id).subscribe({
        next: (data) => {
          this.formGroup = data;
          if (this.formGroup.owners) {
            this.userService.getAdUsers(this.formGroup.owners).subscribe({
              next: (owners) => {
                this.owners = owners;
              },
              error: (error) => {
                this.browserMessagesService.showHttpError(error, 'Daten zu den Verantwortlichen konnten nicht geladen werden');
              }
            });
          } else {
            this.formGroup.owners = [];
          }
          if (this.formGroup.options) {
            this.optionKeys = Object.keys(this.formGroup.options).sort();
            this.optionValues = this.optionKeys.map(key => this.formGroup?.options?.[key] ?? '');
            this.optionKeys = this.optionKeys.map(s => s.replace(s.split('-', 1)[0] + '-', ''));
          } else {
            this.optionKeys = [''];
            this.optionValues = [''];
          }
          if (this.formGroup.links) {
            this.linkKeys = Object.keys(this.formGroup.links).sort();
            this.linkValues = this.linkKeys.map(key => this.formGroup?.links?.[key] ?? '');
            this.linkKeys = this.linkKeys.map(s => s.replace(s.split('-', 1)[0] + '-', ''));
          } else {
            this.linkKeys = [''];
            this.linkValues = [''];
          }
        },
        error: (err) => {
          this.browserMessagesService.showHttpError(err, 'Formulargruppe konnte nicht geladen werden');
        }
      });
    }
  }

  ownersChanged(): void {
    if (this.formGroup) {
      this.formGroup.owners = this.owners.map(o => o.username).filter(s => s) as string[];
    }
  }

  orderOption(from: number, to: number): void {
    this.optionKeys.splice(to, 0, this.optionKeys.splice(from, 1)[0]);
    this.optionValues.splice(to, 0, this.optionValues.splice(from, 1)[0]);
  }

  addOption(): void {
    this.optionKeys.push('');
    this.optionValues.push('');
  }

  removeOption(): void {
    this.removeSpecificOption(this.optionKeys.length - 1);
  }

  removeSpecificOption(pos: number): void {
    if (this.optionKeys.length > pos) {
      this.optionKeys.splice(pos, 1);
      this.optionValues.splice(pos, 1);
    }
  }

  orderLink(from: number, to: number): void {
    this.linkKeys.splice(to, 0, this.linkKeys.splice(from, 1)[0]);
    this.linkValues.splice(to, 0, this.linkValues.splice(from, 1)[0]);
  }

  addLink(): void {
    this.linkKeys.push('');
    this.linkValues.push('');
  }

  removeLink(): void {
    this.removeSpecificOption(this.linkKeys.length - 1);
  }

  removeSpecificLink(pos: number): void {
    if (this.linkKeys.length > pos) {
      this.linkKeys.splice(pos, 1);
      this.linkValues.splice(pos, 1);
    }
  }

  getForms(): void {
    this.formService.getFormsAvailableForGroup().subscribe({
      next: (data) => {
        this.forms = data;
      },
      error: (err) => {
        this.browserMessagesService.showHttpError(err, 'Formulare konnte nicht geladen werden');
      }
    });
  }

  saveFormGroup(): void {
    if (this.formGroup) {
      this.formGroup.options = {};
      for (let i = 0; i < this.optionKeys.length; i++) {
        const key = this.optionKeys[i];
        if (key) {
          this.formGroup.options[i.toLocaleString('de-DE', { minimumIntegerDigits: 2 }) + '-' + key] = this.optionValues[i] ?? '';
        }
      }
      this.formGroup.links = {};
      for (let i = 0; i < this.linkKeys.length; i++) {
        const key = this.linkKeys[i];
        if (key) {
          this.formGroup.links[i.toLocaleString('de-DE', { minimumIntegerDigits: 2 }) + '-' + key] = this.linkValues[i] ?? '';
        }
      }

      if (this.id === 'new') {
        this.formGroupService.createFormGroup(new BodyFormGroup(this.formGroup)).subscribe({
          next: () => {
            this.browserMessagesService.addMessage(new BrowserMessage(
              'Gespeichert',
              'Die Formulargruppe wurde erfolgreich gespeichert.',
              BrowserMessageType.Success
            ));
            this.router.navigateByUrl('/form-groups').then();
          },
          error: (error) => {
            this.browserMessagesService.showHttpError(error, 'Die Formulargruppe konnte nicht gespeichert werden.');
          }
        });
      } else if (this.formGroup.id) {
        this.formGroupService.updateFormGroup(this.formGroup.id, new BodyFormGroup(this.formGroup)).subscribe({
          next: () => {
            this.browserMessagesService.addMessage(new BrowserMessage(
              'Gespeichert',
              'Die Formulargruppe wurde erfolgreich gespeichert.',
              BrowserMessageType.Success
            ));
            this.router.navigateByUrl('/form-groups').then();
          },
          error: (error) => {
            this.browserMessagesService.showHttpError(error, 'Die Formulargruppe konnte nicht gespeichert werden.');
          }
        });
      }
    }
  }

  createTag(): void {
    if (this.formGroup) {
      if (this.id === 'new' && !this.formGroup.tag && this.formGroup.name) {
        this.formGroup.tag = this.formGroup.name.toLowerCase().trim().replace(/[^a-z]+/g, '-').replace(/^-+/g, '').replace(/-+$/g, '').replace(/-+/g, '-');
      }
    }
  }
}
